import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useIsAuthenticated, useAuthUser } from "blitzware-react-sdk";
import Loader from "../ui/loader";
import useUser from "../../api/user";
import { useSyncedUser } from "../../context/SyncedUserContext";
import {
  isBlitzWareAuthError,
  handleBlitzWareAuthError,
} from "../../utils/authErrorHandler";

const AuthWrapper = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const authUser = useAuthUser();
  const userApi = useUser();
  const hasSynced = useRef(false);
  const { updateSyncedUser } = useSyncedUser();

  useEffect(() => {
    // Only check redirects if user is authenticated and user data is loaded
    if (isAuthenticated && authUser && !hasSynced.current) {
      // Sync user with our backend database
      const syncUser = async () => {
        try {
          hasSynced.current = true; // Mark as synced to prevent re-syncing
          const syncedUser = await userApi.syncUserWithExternalAuth(authUser);

          // Check if user has completed onboarding but is not yet accepted
          // Store synced user data for use in other components
          updateSyncedUser(syncedUser);

          if (
            syncedUser?.user &&
            syncedUser.user.onboarding_completed &&
            syncedUser.user.accepted === 0
          ) {
            navigate("/request-submitted");
            return;
          }

          // Check if user is fully validated and can access main app
          if (syncedUser?.user && !syncedUser.user.onboarding_completed) {
            // User exists but hasn't completed onboarding
            navigate("/onboarding");
            return;
          }
          console.log("AuthWrapper - User synced successfully:", syncedUser);
        } catch (error) {
          console.error(
            "AuthWrapper - Error syncing user with backend:",
            error
          );
          console.error("AuthWrapper - Error details:", error.message);
          console.error("AuthWrapper - Error stack:", error.stack);

          // Check if this is a BlitzWare authentication error
          if (isBlitzWareAuthError(error)) {
            handleBlitzWareAuthError(error, navigate);
            return;
          }

          // Reset sync flag on error so we can retry
          hasSynced.current = false;
        }
      };

      syncUser();
    }
  }, [
    isAuthenticated,
    authUser,
    navigate,
    userApi,
    updateSyncedUser,
    location.pathname,
  ]);

  return children;
};

export default AuthWrapper;
